"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MulticallWrapper = void 0;
const debounce_1 = __importDefault(require("lodash/debounce"));
const constants_1 = require("./constants");
const utils_1 = require("./utils");
class MulticallWrapper {
    /**
     * Returns whether a given provider is a multicall-enabled provider.
     * @param provider The provider to check.
     * @returns A boolean indicating whether the given provider is a multicall-enabled provider.
     */
    static isMulticallProvider(provider) {
        if (provider._isMulticallProvider)
            return true;
        return false;
    }
    /**
     * Wraps a given ethers provider to enable automatic call batching.
     * @param provider The underlying provider to use to batch calls.
     * @param delay The delay (in milliseconds) to wait before performing the ongoing batch of calls. Defaults to 16ms.
     * @param maxMulticallDataLength The maximum total calldata length allowed in a multicall batch, to avoid having the RPC backend to revert because of too large (or too long) request. Set to 0 to disable this behavior. Defaults to 200k.
     * @returns The multicall provider, which is a proxy to the given provider, automatically batching any call performed with it.
     */
    static wrap(provider, delay = 16, maxMulticallDataLength = 200000) {
        if (MulticallWrapper.isMulticallProvider(provider))
            return provider; // Do not overwrap when given provider is already a multicall provider.
        // Overload provider
        Object.defineProperties(provider, {
            _isMulticallProvider: {
                value: true,
                writable: false,
                enumerable: true,
                configurable: false,
            },
            _provider: {
                value: provider,
                writable: false,
                enumerable: true,
                configurable: false,
            },
            maxMulticallDataLength: {
                value: maxMulticallDataLength,
                writable: true,
                enumerable: true,
                configurable: true,
            },
            isMulticallEnabled: {
                value: true,
                writable: true,
                enumerable: true,
                configurable: true,
            },
            multicallDelay: {
                get: function () {
                    return this._multicallDelay;
                },
                set: function (delay) {
                    var _a;
                    (_a = this._debouncedPerformMulticall) === null || _a === void 0 ? void 0 : _a.flush();
                    this._multicallDelay = delay;
                    this._debouncedPerformMulticall = (0, debounce_1.default)(this._performMulticall, delay);
                },
                enumerable: true,
                configurable: false,
            },
        });
        const multicallProvider = provider;
        // Define execution context
        let queuedCalls = [];
        multicallProvider._performMulticall = function () {
            return __awaiter(this, void 0, void 0, function* () {
                const _queuedCalls = [...queuedCalls];
                if (queuedCalls.length === 0)
                    return;
                queuedCalls = [];
                const blockTagCalls = _queuedCalls.reduce((acc, queuedCall) => {
                    var _a;
                    const blockTag = queuedCall.blockTag.toString();
                    return Object.assign(Object.assign({}, acc), { [blockTag]: [queuedCall].concat((_a = acc[blockTag]) !== null && _a !== void 0 ? _a : []) });
                }, {});
                yield Promise.all(Object.values(blockTagCalls).map((blockTagQueuedCalls) => __awaiter(this, void 0, void 0, function* () {
                    const callStructs = blockTagQueuedCalls.map(({ to, data }) => ({
                        target: to,
                        callData: data,
                    }));
                    // Split call in parts of max length to avoid too-long requests
                    let currentLength = 0;
                    const calls = [[]];
                    callStructs.forEach((callStruct) => {
                        const newLength = currentLength + callStruct.callData.length;
                        if (this.maxMulticallDataLength > 0 && newLength > this.maxMulticallDataLength) {
                            currentLength = callStruct.callData.length;
                            calls.push([]);
                        }
                        else
                            currentLength = newLength;
                        calls[calls.length - 1].push(callStruct);
                    });
                    const { blockTag, multicall } = blockTagQueuedCalls[0];
                    try {
                        const res = (yield Promise.all(calls.map((call) => multicall.tryAggregate.staticCall(false, call, { blockTag })))).flat();
                        if (res.length !== callStructs.length)
                            throw new Error(`Unexpected multicall response length: received ${res.length}; expected ${callStructs.length}`);
                        blockTagQueuedCalls.forEach(({ resolve }, i) => {
                            resolve(res[i].returnData);
                        });
                    }
                    catch (error) {
                        blockTagQueuedCalls.forEach(({ reject }) => {
                            reject(error);
                        });
                    }
                })));
            });
        };
        // Overload multicall provider delay
        multicallProvider.multicallDelay = delay;
        // Expose `Provider.fetchNetwork` to fetch & update the network cache when needed
        const getNetwork = provider.getNetwork.bind(provider);
        multicallProvider.fetchNetwork = function fetchNetwork() {
            return __awaiter(this, void 0, void 0, function* () {
                this._networkPromise = getNetwork();
                return this._networkPromise;
            });
        };
        multicallProvider.fetchNetwork();
        // Overload `Provider._detectNetwork` to disable polling the network at each RPC call
        multicallProvider._detectNetwork = function _detectNetwork() {
            return __awaiter(this, void 0, void 0, function* () {
                return this._networkPromise;
            });
        };
        // Overload `Provider._perform`
        const _perform = provider._perform.bind(provider);
        multicallProvider._perform = function (req) {
            return __awaiter(this, void 0, void 0, function* () {
                if (req.method !== "call" || !this.isMulticallEnabled)
                    return _perform(req);
                const { transaction: { to, data }, blockTag, } = req;
                const network = yield this._networkPromise;
                const blockNumber = (0, utils_1.getBlockNumber)(blockTag);
                const multicall = (0, utils_1.getMulticall)(blockNumber, Number(network.chainId), provider);
                if (!to || !data || multicall == null || constants_1.multicallAddresses.has(to.toString().toLowerCase()))
                    return _perform(req);
                this._debouncedPerformMulticall();
                return new Promise((resolve, reject) => {
                    queuedCalls.push({
                        to,
                        data,
                        blockTag,
                        multicall,
                        resolve,
                        reject,
                    });
                });
            });
        };
        return multicallProvider;
    }
}
exports.MulticallWrapper = MulticallWrapper;
exports.default = MulticallWrapper;
